/**
 * Report — the tenant-global Report Type registry (ADR-0060 S1).
 *
 * Master/detail: the left rail lists the Types; the detail edits name/
 * description + the recursive SCHEMA TREE (the typed DTO contract band
 * layouts bind to, ADR-0061). Node `id`s are the stable binding keys —
 * the tree builder edits them explicitly; labels are display-only.
 * Physical reports + the designer + providers land in the next slices.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  FileType2,
  List,
  Plus,
  Printer,
  Trash2,
  Wand2,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { CodeField } from "@/components/apps/pageBuilder/CodeField";
import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import { appsApi } from "@/lib/api-apps";
import { datasetsApi } from "@/lib/api-datasets";
import {
  ConditionRows,
  rowsToSpec,
  specToRows,
  type ConditionRow,
} from "@/components/datasets/conditionBuilder";
import {
  REPORT_CONTAINER_TYPES,
  REPORT_SCALAR_TYPES,
  reportTypesApi,
  type ReportDatasetFilter,
  type ReportNodeType,
  type ReportParam,
  type ReportProvider,
  type ReportType,
  type ReportTypeNode,
} from "@/lib/api-reports";
import {
  REPORT_HANDLER_SKELETON,
  reportProviderDts,
  type ReportTableRef,
} from "@/lib/reportTypeDts";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

// ---------------------------------------------------------------------------
// Pure tree helpers — nodes are addressed by PATH (index chain), so edits are
// simple immutable rewrites and need no synthetic ids.
// ---------------------------------------------------------------------------

type Path = number[];

export function updateNodeAt(
  nodes: ReportTypeNode[],
  path: Path,
  patch: Partial<ReportTypeNode>,
): ReportTypeNode[] {
  const [head, ...rest] = path;
  return nodes.map((n, i) => {
    if (i !== head) return n;
    if (rest.length === 0) return { ...n, ...patch };
    return { ...n, children: updateNodeAt(n.children ?? [], rest, patch) };
  });
}

export function removeNodeAt(nodes: ReportTypeNode[], path: Path): ReportTypeNode[] {
  const [head, ...rest] = path;
  if (rest.length === 0) return nodes.filter((_, i) => i !== head);
  return nodes.map((n, i) =>
    i === head ? { ...n, children: removeNodeAt(n.children ?? [], rest) } : n,
  );
}

export function addChildAt(
  nodes: ReportTypeNode[],
  path: Path | null,
  child: ReportTypeNode,
): ReportTypeNode[] {
  if (path === null) return [...nodes, child];
  const [head, ...rest] = path;
  return nodes.map((n, i) => {
    if (i !== head) return n;
    if (rest.length === 0) return { ...n, children: [...(n.children ?? []), child] };
    return { ...n, children: addChildAt(n.children ?? [], rest, child) };
  });
}

/** Next free identifier-shaped id (`campo_N`) across the WHOLE tree. */
export function nextNodeId(nodes: ReportTypeNode[], prefix = "campo"): string {
  const ids = new Set<string>();
  const walk = (items: ReportTypeNode[]) => {
    for (const n of items) {
      ids.add(n.id);
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  let i = 1;
  while (ids.has(`${prefix}_${i}`)) i += 1;
  return `${prefix}_${i}`;
}

const TYPE_OPTIONS: ReportNodeType[] = [...REPORT_SCALAR_TYPES, ...REPORT_CONTAINER_TYPES];

function isContainer(t: ReportNodeType): boolean {
  return t === "object" || t === "collection";
}

/** The three explicit "add" kinds — a fresh node per kind, with a kind-shaped
 *  id prefix so `campo_1` / `oggetto_1` / `lista_1` read naturally. */
export function newNode(
  nodes: ReportTypeNode[],
  kind: "field" | "object" | "collection",
): ReportTypeNode {
  if (kind === "object")
    return {
      id: nextNodeId(nodes, "oggetto"),
      label: "Nuovo oggetto",
      type: "object",
      children: [],
    };
  if (kind === "collection")
    return {
      id: nextNodeId(nodes, "lista"),
      label: "Nuova lista",
      type: "collection",
      children: [],
    };
  return { id: nextNodeId(nodes, "campo"), label: "Nuovo campo", type: "text" };
}

type AddKind = "field" | "object" | "collection";

/** The explicit, ALWAYS-VISIBLE add trio (Campo · Oggetto · Lista). The first
 *  cut hid a hover-only "+ Campo" and forced "add text then flip the type
 *  select" to nest — the owner couldn't create nested structures from the UI. */
function AddChildButtons({
  onAdd,
  compact,
}: {
  onAdd: (kind: AddKind) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const cls = compact ? "h-6 px-1.5 text-[11px]" : "h-7 px-2 text-xs";
  return (
    <span className="flex shrink-0 items-center gap-1">
      <Button variant="outline" size="sm" className={cls} onClick={() => onAdd("field")}>
        <Plus className="mr-0.5 h-3 w-3" />
        {t("reports.addField", "Campo")}
      </Button>
      <Button variant="outline" size="sm" className={cls} onClick={() => onAdd("object")}>
        <Boxes className="mr-0.5 h-3 w-3" />
        {t("reports.addObject", "Oggetto")}
      </Button>
      <Button variant="outline" size="sm" className={cls} onClick={() => onAdd("collection")}>
        <List className="mr-0.5 h-3 w-3" />
        {t("reports.addCollection", "Lista")}
      </Button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tree builder (recursive)
// ---------------------------------------------------------------------------

function NodeRow({
  node,
  path,
  depth,
  canWrite,
  onPatch,
  onRemove,
  onAddChild,
}: {
  node: ReportTypeNode;
  path: Path;
  depth: number;
  canWrite: boolean;
  onPatch: (path: Path, patch: Partial<ReportTypeNode>) => void;
  onRemove: (path: Path) => void;
  onAddChild: (path: Path, kind: "field" | "object" | "collection") => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(true);
  const container = isContainer(node.type);

  return (
    <div>
      <div
        className="group flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface"
        style={{ paddingLeft: `${depth * 20 + 4}px` }}
      >
        {container ? (
          <button
            type="button"
            aria-label={open ? "Comprimi" : "Espandi"}
            onClick={() => setOpen((o) => !o)}
            className="text-muted-foreground"
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
          </button>
        ) : (
          <span className="w-3.5" />
        )}
        {container ? (
          node.type === "collection" ? (
            <List className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.75} />
          ) : (
            <Boxes className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.75} />
          )
        ) : (
          <FileType2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        )}
        <Input
          value={node.id}
          disabled={!canWrite}
          onChange={(e) => onPatch(path, { id: e.target.value })}
          aria-label={t("reports.nodeId", "ID campo")}
          className="h-7 w-40 font-mono text-xs"
        />
        <Input
          value={node.label}
          disabled={!canWrite}
          onChange={(e) => onPatch(path, { label: e.target.value })}
          aria-label={t("reports.nodeLabel", "Etichetta")}
          className="h-7 flex-1 text-xs"
        />
        <select
          value={node.type}
          disabled={!canWrite}
          aria-label={t("reports.nodeType", "Tipo")}
          onChange={(e) => {
            const next = e.target.value as ReportNodeType;
            // Dropping to a scalar clears children (the backend rejects
            // scalar-with-children — mirror it here so save can't 422).
            onPatch(path, isContainer(next) ? { type: next } : { type: next, children: [] });
          }}
          className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {canWrite && (
          <>
            {container && (
              <AddChildButtons
                compact
                onAdd={(kind) => {
                  setOpen(true);
                  onAddChild(path, kind);
                }}
              />
            )}
            <button
              type="button"
              aria-label={t("reports.removeNode", "Rimuovi campo")}
              onClick={() => onRemove(path)}
              className="text-muted-foreground/60 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </>
        )}
      </div>
      {container && open && (
        <div>
          {(node.children ?? []).map((child, i) => (
            <NodeRow
              key={`${path.join(".")}-${i}`}
              node={child}
              path={[...path, i]}
              depth={depth + 1}
              canWrite={canWrite}
              onPatch={onPatch}
              onRemove={onRemove}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail — name/description + the tree, buffered locally, saved on demand
// ---------------------------------------------------------------------------

function TypeDetail({
  row,
  canWrite,
  canDelete,
  onDeleted,
}: {
  row: ReportType;
  canWrite: boolean;
  canDelete: boolean;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = React.useState(row.name);
  const [description, setDescription] = React.useState(row.description ?? "");
  const [nodes, setNodes] = React.useState<ReportTypeNode[]>(row.schema);
  const [error, setError] = React.useState<string | null>(null);
  // Provider buffer (ADR-0060 S2 + S6) — kind "" = none configured.
  const [providerKind, setProviderKind] = React.useState<
    "" | "code" | "passthrough" | "dataset" | "sql"
  >(row.provider?.kind ?? "");
  const [codeTs, setCodeTs] = React.useState(
    row.provider?.code_ts ?? row.provider?.code ?? REPORT_HANDLER_SKELETON,
  );
  const [tsErrors, setTsErrors] = React.useState(0);
  // S6 — the flat set-returning kinds.
  const [datasetId, setDatasetId] = React.useState(row.provider?.dataset_id ?? "");
  // The visual QueryBuilder's SHAPE over the dataset (ADR-0062 S4/C2).
  const [datasetColumns, setDatasetColumns] = React.useState<string[]>(row.provider?.columns ?? []);
  const [datasetFilters, setDatasetFilters] = React.useState<ReportDatasetFilter[]>(
    row.provider?.filters ?? [],
  );
  const [credentialId, setCredentialId] = React.useState<string | null>(
    row.provider?.credential_id ?? null,
  );
  const [sqlText, setSqlText] = React.useState(row.provider?.sql ?? "");
  // The declared, typed contract for the sql `:name` binds (ADR-0062 S1).
  const [sqlParams, setSqlParams] = React.useState<ReportParam[]>(row.provider?.params ?? []);
  // R2b — per-param dynamic-options declarations (`options_from`), keyed by
  // param NAME. The TS `interface Input` can't express an options source, so
  // this map is the authoring surface; the save merges it back into `params`.
  const [paramOptions, setParamOptions] = React.useState<Record<string, ParamOptionsFrom>>(() =>
    optionsMapOf(row.provider?.params),
  );
  // R1 IA: the detail is TABBED — designing the data source (contract +
  // provider) is a different activity from managing the runtime-many prints.
  const [tab, setTab] = React.useState<"schema" | "provider" | "reports">("schema");

  // Re-seed the buffer when another Type is selected (id change), never on
  // background refetches (they would clobber in-progress edits).
  React.useEffect(() => {
    setName(row.name);
    setDescription(row.description ?? "");
    setNodes(row.schema);
    setError(null);
    setProviderKind(row.provider?.kind ?? "");
    setCodeTs(row.provider?.code_ts ?? row.provider?.code ?? REPORT_HANDLER_SKELETON);
    setTsErrors(0);
    setDatasetId(row.provider?.dataset_id ?? "");
    setDatasetColumns(row.provider?.columns ?? []);
    setDatasetFilters(row.provider?.filters ?? []);
    setCredentialId(row.provider?.credential_id ?? null);
    setSqlText(row.provider?.sql ?? "");
    setSqlParams(row.provider?.params ?? []);
    setParamOptions(optionsMapOf(row.provider?.params));
    setTab("schema");
  }, [row.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      // Build the provider from the buffer. The `code` kind TRANSPILES the TS
      // source (compileAppLogic — the ADR-0054 compiler; a `handler(ctx,input)`
      // shape gets the `return handler(ctx, ctx.input)` bridge the read-only
      // runtime runs). A TS syntax error / Monaco semantic errors BLOCK the save.
      let provider: ReportProvider | null = null;
      if (providerKind === "passthrough") {
        provider = { kind: "passthrough" };
      } else if (providerKind === "dataset") {
        provider = {
          kind: "dataset",
          dataset_id: datasetId || null,
          columns: datasetColumns,
          filters: datasetFilters.filter((f) => f.column && f.op),
        };
      } else if (providerKind === "sql") {
        provider = {
          kind: "sql",
          credential_id: credentialId,
          sql: sqlText,
          params: sqlParams.filter((p) => p.name.trim()).map((p) => withOptions(p, paramOptions)),
        };
      } else if (providerKind === "code") {
        if (tsErrors > 0) throw new Error(t("reports.tsErrors", "Errore TypeScript nel codice"));
        const { compileAppLogic } = await import("@/lib/appLogicTs");
        const compiled = await compileAppLogic(codeTs);
        if (!compiled.ok)
          throw new Error(
            `${t("reports.tsErrors", "Errore TypeScript nel codice")}: ${compiled.diagnostics[0]?.message ?? ""}`,
          );
        provider = {
          kind: "code",
          code: compiled.codeJs,
          code_ts: codeTs,
          // R2: the declared `interface Input` IS the render-param contract —
          // persisted so every guided form (designer preview, admin render,
          // portal print dialog) knows what `ctx.input` expects. No Input
          // declared → [] (an honest "no params"). R2b/R2c: the options
          // sources ride from the "Opzioni dinamiche" editor (the TS type
          // can't express them); an incomplete declaration is dropped, never
          // a half-row (the backend validator would 422 it).
          params: (compiled.contract.input ?? []).map((p) => withOptions(p, paramOptions)),
        };
      }
      return reportTypesApi.update(row.id, {
        name,
        description: description || null,
        schema: nodes,
        provider,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["report-types"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: () => reportTypesApi.remove(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["report-types"] });
      onDeleted();
    },
    onError: (e: Error) => setError(e.message),
  });

  const patch = (path: Path, p: Partial<ReportTypeNode>) =>
    setNodes((n) => updateNodeAt(n, path, p));
  const remove = (path: Path) => setNodes((n) => removeNodeAt(n, path));
  const addChild = (path: Path | null, kind: "field" | "object" | "collection" = "field") =>
    setNodes((n) => addChildAt(n, path, newNode(n, kind)));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* Header — icon + title + right-aligned actions (the app-pane
          master/detail look: Salva primary, Elimina as the red text-button). */}
      <div className="flex items-center gap-2 border-b border-border/50 pb-3">
        <FileType2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{name || row.name}</span>
        {canWrite && (
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {t("common.save", "Salva")}
          </Button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t("reports.confirmDelete", "Eliminare questo tipo report?")))
                del.mutate();
            }}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-danger hover:bg-danger/10"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("common.delete", "Elimina")}
          </button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="rt-name">
            {t("reports.typeName", "Nome")}
          </label>
          <Input
            id="rt-name"
            value={name}
            disabled={!canWrite}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground" htmlFor="rt-desc">
            {t("reports.typeDescription", "Descrizione")}
          </label>
          <Input
            id="rt-desc"
            value={description}
            disabled={!canWrite}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}

      {/* R1 IA — tabbed detail: contract | data source | prints. */}
      <div className="flex items-center gap-1 border-b border-border pb-2">
        {(
          [
            ["schema", t("reports.tabSchema", "Struttura dati")],
            ["provider", t("reports.tabProvider", "Origine dati")],
            ["reports", t("reports.tabReports", "Report")],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              tab === k
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-surface",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "schema" && (
        <>
          <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <div className="text-sm font-medium">
                {t("reports.schemaTitle", "Struttura dati (contratto)")}
              </div>
              {canWrite && <AddChildButtons onAdd={(kind) => addChild(null, kind)} />}
            </div>
            <div className="p-2">
              {nodes.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t(
                    "reports.emptySchema",
                    "Nessun campo. Aggiungi i campi di testata e le collection (es. righe documento).",
                  )}
                </div>
              ) : (
                nodes.map((n, i) => (
                  <NodeRow
                    key={i}
                    node={n}
                    path={[i]}
                    depth={0}
                    canWrite={canWrite}
                    onPatch={patch}
                    onRemove={remove}
                    onAddChild={addChild}
                  />
                ))
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              "reports.schemaHint",
              "L'ID è la chiave stabile su cui i layout si agganciano; l'etichetta è solo visuale. Una collection è un elenco ripetuto (righe), un object un record annidato.",
            )}
          </p>
        </>
      )}

      {/* Provider (ADR-0060 S2) — how the DTO gets filled. */}
      {tab === "provider" && (
        <div className="rounded-lg border border-border">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="text-sm font-medium">{t("reports.providerTitle", "Origine dati")}</div>
            <select
              value={providerKind}
              disabled={!canWrite}
              aria-label={t("reports.providerKind", "Tipo origine")}
              onChange={(e) =>
                setProviderKind(e.target.value as "" | "code" | "passthrough" | "dataset" | "sql")
              }
              className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
            >
              <option value="">{t("reports.providerNone", "Nessuna")}</option>
              <option value="code">{t("reports.providerCode", "Codice (TypeScript)")}</option>
              <option value="dataset">
                {t("reports.providerDataset", "Dataset del catalogo")}
              </option>
              <option value="sql">{t("reports.providerSql", "SQL (credenziale DB)")}</option>
              <option value="passthrough">
                {t("reports.providerPassthrough", "Passthrough (dati dal chiamante)")}
              </option>
            </select>
          </div>
          {providerKind === "passthrough" && (
            <p className="px-3 py-3 text-xs text-muted-foreground">
              {t(
                "reports.passthroughHint",
                "Il chiamante (agente, workflow, REST) passa direttamente le istanze del DTO; vengono validate contro la struttura del tipo.",
              )}
            </p>
          )}
          {providerKind === "code" && (
            <>
              <CodeProviderEditor
                typeId={row.id}
                nodes={nodes}
                codeTs={codeTs}
                onCodeTs={setCodeTs}
                onDiagnostics={setTsErrors}
              />
              <ParamOptionsEditor
                params={row.param_schema}
                value={paramOptions}
                onChange={setParamOptions}
                canWrite={canWrite}
              />
            </>
          )}
          {providerKind === "dataset" && (
            <DatasetProviderEditor
              datasetId={datasetId}
              columns={datasetColumns}
              filters={datasetFilters}
              canWrite={canWrite}
              onDatasetId={setDatasetId}
              onColumns={setDatasetColumns}
              onFilters={setDatasetFilters}
              onDeduce={(schema) => setNodes(schema)}
            />
          )}
          {providerKind === "sql" && (
            <>
              <SqlProviderEditor
                credentialId={credentialId}
                sql={sqlText}
                params={sqlParams}
                canWrite={canWrite}
                onCredentialId={setCredentialId}
                onSql={setSqlText}
                onParams={setSqlParams}
                onDeduce={(schema) => setNodes(schema)}
              />
              <ParamOptionsEditor
                params={row.param_schema}
                value={paramOptions}
                onChange={setParamOptions}
                canWrite={canWrite}
              />
            </>
          )}
        </div>
      )}

      {/* Physical reports (ADR-0061 S3) — the runtime-many prints. */}
      {tab === "reports" && (
        <ReportsSection
          typeId={row.id}
          paramNames={row.param_names}
          canWrite={canWrite}
          canDelete={canDelete}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// S6 provider editors — dataset / sql, each with "Deduci schema" (the low-code
// on-ramp: run one sample, adopt the resulting node tree as the Type schema).
// ---------------------------------------------------------------------------

function DeduceButton({
  provider,
  disabled,
  onDeduce,
}: {
  provider: ReportProvider;
  disabled: boolean;
  onDeduce: (schema: ReportTypeNode[]) => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = React.useState<string | null>(null);
  const deduce = useMutation({
    mutationFn: () => reportTypesApi.deduceSchema(provider),
    onSuccess: (res) => {
      setError(null);
      if (
        res.schema.length === 0 ||
        window.confirm(
          t(
            "reports.deduceConfirm",
            "Sostituire lo schema del tipo con i campi dedotti dall'origine?",
          ),
        )
      )
        onDeduce(res.schema);
    },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || deduce.isPending}
        onClick={() => deduce.mutate()}
      >
        <Wand2 className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} />
        {deduce.isPending
          ? t("reports.deducing", "Deduco…")
          : t("reports.deduceSchema", "Deduci schema")}
      </Button>
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

function DatasetProviderEditor({
  datasetId,
  columns,
  filters,
  canWrite,
  onDatasetId,
  onColumns,
  onFilters,
  onDeduce,
}: {
  datasetId: string;
  columns: string[];
  filters: ReportDatasetFilter[];
  canWrite: boolean;
  onDatasetId: (id: string) => void;
  onColumns: (columns: string[]) => void;
  onFilters: (filters: ReportDatasetFilter[]) => void;
  onDeduce: (schema: ReportTypeNode[]) => void;
}) {
  const { t } = useTranslation();
  const dsQ = useQuery({
    queryKey: ["datasets", "report-provider"],
    queryFn: () => datasetsApi.list(),
  });
  // The dataset's projectable columns — drive both the projection checklist AND
  // the filter field pickers (the visual QueryBuilder, ADR-0062 S4/C2).
  const fieldsQ = useQuery({
    queryKey: ["datasets", datasetId, "fields", "report-provider"],
    queryFn: () => datasetsApi.fields(datasetId),
    enabled: !!datasetId,
    retry: false,
  });
  const allNames = React.useMemo(() => (fieldsQ.data ?? []).map((f) => f.name), [fieldsQ.data]);
  // `columns` empty = ALL projected (the SQL-safe default); a non-empty list is an
  // explicit subset. Toggling off a column while "all" materialises the rest.
  const allSelected = columns.length === 0;
  const isChecked = (name: string) => allSelected || columns.includes(name);
  const toggleColumn = (name: string) => {
    const current = allSelected ? allNames : columns;
    const next = current.includes(name) ? current.filter((c) => c !== name) : [...current, name];
    // Collapse a full selection back to [] (= all) so the wire stays clean.
    onColumns(next.length === allNames.length ? [] : next);
  };

  // The flat AND filter ↔ the shared ConditionRows `{match, conditions}` shape.
  const rows: ConditionRow[] = specToRows({
    match: "all",
    conditions: filters.map((f) => ({ field: f.column, op: f.op, value: f.value })),
  });
  const onRows = (next: ConditionRow[]) =>
    onFilters(
      (rowsToSpec("all", next)?.conditions ?? []).map((c) => ({
        column: c.field,
        op: c.op,
        value: c.value,
      })),
    );

  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      <select
        value={datasetId}
        disabled={!canWrite}
        aria-label={t("reports.providerDatasetPick", "Dataset")}
        onChange={(e) => onDatasetId(e.target.value)}
        className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm"
      >
        <option value="">{t("reports.providerDatasetNone", "— scegli un dataset —")}</option>
        {(dsQ.data ?? []).map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      <p className="text-[11px] text-muted-foreground">
        {t(
          "reports.providerDatasetHint",
          "Ogni riga del dataset diventa un'istanza del report. I filtri del dataset (Semantica) si applicano automaticamente; qui aggiungi proiezione colonne e filtri riga del report.",
        )}
      </p>

      {datasetId && allNames.length > 0 && (
        <>
          <div className="rounded-md border border-border">
            <div className="border-b border-border px-2 py-1.5 text-xs font-medium">
              {t("reports.datasetColumns", "Colonne (proiezione)")}
            </div>
            <div className="flex max-h-44 flex-col gap-1 overflow-y-auto p-2">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={!canWrite}
                  onChange={() => onColumns([])}
                />
                {t("reports.datasetAllColumns", "Tutte le colonne")}
              </label>
              {allNames.map((name) => (
                <label key={name} className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={isChecked(name)}
                    disabled={!canWrite}
                    onChange={() => toggleColumn(name)}
                  />
                  <span className="font-mono">{name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-border">
            <div className="border-b border-border px-2 py-1.5 text-xs font-medium">
              {t("reports.datasetFilters", "Filtri riga (AND)")}
            </div>
            <div className="p-2">
              <ConditionRows
                fields={allNames.map((name) => ({ name }))}
                rows={rows}
                match="all"
                onRowsChange={canWrite ? onRows : () => {}}
                onMatchChange={() => {}}
                emptyHint={t(
                  "reports.datasetFilterNone",
                  "Nessun filtro — aggiungi una condizione per restringere le righe.",
                )}
              />
            </div>
          </div>
        </>
      )}
      {datasetId && fieldsQ.isError && (
        <p className="text-[11px] text-danger">
          {t(
            "reports.datasetFieldsError",
            "Impossibile leggere le colonne del dataset (sorgente non raggiungibile?).",
          )}
        </p>
      )}

      <DeduceButton
        provider={{ kind: "dataset", dataset_id: datasetId || null }}
        disabled={!canWrite || !datasetId}
        onDeduce={onDeduce}
      />
    </div>
  );
}

function SqlProviderEditor({
  credentialId,
  sql,
  params,
  canWrite,
  onCredentialId,
  onSql,
  onParams,
  onDeduce,
}: {
  credentialId: string | null;
  sql: string;
  params: ReportParam[];
  canWrite: boolean;
  onCredentialId: (id: string | null) => void;
  onSql: (sql: string) => void;
  onParams: (params: ReportParam[]) => void;
  onDeduce: (schema: ReportTypeNode[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <CredentialPicker
        value={credentialId}
        onChange={onCredentialId}
        type={["postgres", "mysql", "sqlserver"]}
        disabled={!canWrite}
        placeholder={t("reports.providerSqlCred", "Credenziale DB (anche via Gateway)")}
      />
      <textarea
        value={sql}
        disabled={!canWrite}
        onChange={(e) => onSql(e.target.value)}
        rows={5}
        spellCheck={false}
        placeholder={"SELECT numero, totale FROM ordini WHERE cliente = :cliente"}
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
      />
      <p className="text-[11px] text-muted-foreground">
        {t(
          "reports.providerSqlHint",
          "Solo SELECT/WITH, statement singolo, engine in sola lettura. I parametri :nome vengono legati dai parametri del report (mai interpolati).",
        )}
      </p>
      <SqlParamsEditor params={params} canWrite={canWrite} onParams={onParams} />
      <DeduceButton
        provider={{ kind: "sql", credential_id: credentialId, sql }}
        disabled={!canWrite || !credentialId || !sql.trim()}
        onDeduce={onDeduce}
      />
    </div>
  );
}

const REPORT_PARAM_TYPES: ReportParam["type"][] = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
];

// Declare the typed contract for the sql `:name` binds (ADR-0062 S1). An
// UNDECLARED bind still works as a required string (the backend derives the
// schema from the binds), so this editor is optional — it only adds
// type/required/description on top. A declared `number`/`boolean` is coerced;
// everything else binds as a RAW STRING (the DB infers the column type).
function SqlParamsEditor({
  params,
  canWrite,
  onParams,
}: {
  params: ReportParam[];
  canWrite: boolean;
  onParams: (params: ReportParam[]) => void;
}) {
  const { t } = useTranslation();
  const patch = (i: number, p: Partial<ReportParam>) =>
    onParams(params.map((row, idx) => (idx === i ? { ...row, ...p } : row)));
  const remove = (i: number) => onParams(params.filter((_, idx) => idx !== i));
  const add = () => onParams([...params, { name: "", type: "string", required: true }]);

  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
        <div className="text-xs font-medium">{t("reports.paramsTitle", "Parametri (:nome)")}</div>
        {canWrite && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={add}>
            {t("reports.paramAdd", "+ Parametro")}
          </Button>
        )}
      </div>
      {params.length === 0 ? (
        <p className="px-2 py-2 text-[11px] text-muted-foreground">
          {t(
            "reports.paramsEmpty",
            "Nessun parametro dichiarato: i bind :nome vengono legati come stringhe obbligatorie.",
          )}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5 p-2">
          {params.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input
                value={p.name}
                disabled={!canWrite}
                onChange={(e) => patch(i, { name: e.target.value })}
                placeholder={t("reports.paramName", "nome")}
                className="h-7 flex-1 font-mono text-xs"
              />
              <select
                value={p.type}
                disabled={!canWrite}
                aria-label={t("reports.paramType", "tipo")}
                onChange={(e) => patch(i, { type: e.target.value as ReportParam["type"] })}
                className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
              >
                {REPORT_PARAM_TYPES.map((ty) => (
                  <option key={ty} value={ty}>
                    {ty}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={p.required !== false}
                  disabled={!canWrite}
                  onChange={(e) => patch(i, { required: e.target.checked })}
                />
                {t("reports.paramRequired", "obbl.")}
              </label>
              {canWrite && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-danger"
                  onClick={() => remove(i)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports (layouts) — list + raw-JSON layout editor + HTML/PDF preview.
// The S4 designer canvas replaces the JSON editor; until then this is the
// dev-mode authoring escape hatch over the SAME validated ReportAst.
// ---------------------------------------------------------------------------

const DEFAULT_LAYOUT: Record<string, unknown> = {
  page_setup: { kind: "document", format: "A4", orientation: "portrait" },
  bands: [{ kind: "detail", height_mm: 40, controls: [] }],
};

function ReportsSection({
  typeId,
  paramNames,
  canWrite,
  canDelete,
}: {
  typeId: string;
  /** The Type's declared render params (derived from the provider's `:name`
   *  binds) — rendered as a GUIDED per-param form, never raw JSON (R1). */
  paramNames: string[];
  canWrite: boolean;
  canDelete: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const reportsQ = useQuery({
    queryKey: ["report-types", typeId, "reports"],
    queryFn: () => reportTypesApi.listReports(typeId),
  });
  const reports = reportsQ.data ?? [];
  const [error, setError] = React.useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = React.useState<string | null>(null);
  // Guided render params — values bind as RAW STRINGS (the server infers the
  // type from the column; client-side type-guessing is the hard-error path).
  const [paramValues, setParamValues] = React.useState<Record<string, string>>({});

  const invalidate = () => qc.invalidateQueries({ queryKey: ["report-types", typeId, "reports"] });

  const create = useMutation({
    mutationFn: () => {
      const names = new Set(reports.map((r) => r.name));
      let i = 1;
      while (names.has(`Report ${i}`)) i += 1;
      return reportTypesApi.createReport(typeId, {
        name: `Report ${i}`,
        layout: DEFAULT_LAYOUT,
      });
    },
    onSuccess: (r) => {
      setError(null);
      void invalidate();
      // Canvas-first: a fresh print goes straight to the designer.
      navigate(`/report-types/${typeId}/reports/${r.id}/designer`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const rename = useMutation({
    mutationFn: (r: { id: string; name: string }) =>
      reportTypesApi.updateReport(typeId, r.id, { name: r.name }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: (reportId: string) => reportTypesApi.removeReport(typeId, reportId),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => setError(e.message),
  });

  const renderParams = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const name of paramNames) {
      const v = paramValues[name];
      if (v !== undefined && v !== "") out[name] = v;
    }
    return out;
  };

  const preview = useMutation({
    mutationFn: (reportId: string) =>
      reportTypesApi.renderHtml(typeId, reportId, { params: renderParams() }),
    onSuccess: (html) => {
      setError(null);
      setPreviewHtml(html);
    },
    onError: (e: Error) => setError(e.message),
  });

  const pdf = useMutation({
    mutationFn: (r: { id: string; name: string }) =>
      reportTypesApi.renderPdf(typeId, r.id, r.name, { params: renderParams() }),
    onSuccess: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="text-sm font-medium">{t("reports.reportsTitle", "Report (stampe)")}</div>
        {canWrite && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            <Plus className="mr-0.5 h-3 w-3" />
            {t("reports.newReport", "Nuovo report")}
          </Button>
        )}
      </div>
      {error && <div className="px-3 py-2 text-xs text-danger">{error}</div>}
      {paramNames.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 border-b border-border bg-surface/40 px-3 py-2">
          <span className="pb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {t("reports.renderParams", "Parametri")}
          </span>
          {paramNames.map((name) => (
            <label key={name} className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              {name}
              <Input
                value={paramValues[name] ?? ""}
                onChange={(e) => setParamValues((p) => ({ ...p, [name]: e.target.value }))}
                className="h-7 w-40 text-xs"
              />
            </label>
          ))}
        </div>
      )}
      {reports.length === 0 ? (
        <p className="px-3 py-4 text-center text-xs text-muted-foreground">
          {t("reports.noReports", "Nessun report. Crea il primo layout su questo tipo.")}
        </p>
      ) : (
        <div className="divide-y divide-border">
          {reports.map((r) => (
            <ReportRow
              key={r.id}
              name={r.name}
              canWrite={canWrite}
              canDelete={canDelete}
              busyPreview={preview.isPending}
              busyPdf={pdf.isPending}
              onRename={(name) => rename.mutate({ id: r.id, name })}
              onDesigner={() => navigate(`/report-types/${typeId}/reports/${r.id}/designer`)}
              onPreview={() => preview.mutate(r.id)}
              onPdf={() => pdf.mutate({ id: r.id, name: r.name })}
              onDelete={() => {
                if (window.confirm(t("reports.confirmDeleteReport", "Eliminare il report?")))
                  del.mutate(r.id);
              }}
            />
          ))}
        </div>
      )}

      {previewHtml !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
          <div className="flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <div className="text-sm font-medium">
                {t("reports.previewTitle", "Anteprima report")}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPreviewHtml(null)}
              >
                {t("common.close", "Chiudi")}
              </Button>
            </div>
            {/* allow-scripts: the document carries the compiler's screen
                pagination shell; NO allow-same-origin (opaque origin). */}
            <iframe
              title={t("reports.previewTitle", "Anteprima report")}
              srcDoc={previewHtml}
              sandbox="allow-scripts"
              className="h-full w-full flex-1 bg-[#e8e8e8]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** One print row — clean actions, inline rename (local buffer, saved on
 *  blur/Enter when changed — never clobbered by background refetches). */
function ReportRow({
  name,
  canWrite,
  canDelete,
  busyPreview,
  busyPdf,
  onRename,
  onDesigner,
  onPreview,
  onPdf,
  onDelete,
}: {
  name: string;
  canWrite: boolean;
  canDelete: boolean;
  busyPreview: boolean;
  busyPdf: boolean;
  onRename: (name: string) => void;
  onDesigner: () => void;
  onPreview: () => void;
  onPdf: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [buf, setBuf] = React.useState(name);
  React.useEffect(() => setBuf(name), [name]);
  const commit = () => {
    const v = buf.trim();
    if (v && v !== name) onRename(v);
    else setBuf(name);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Printer className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.75} />
      <Input
        value={buf}
        disabled={!canWrite}
        onChange={(e) => setBuf(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setBuf(name);
        }}
        aria-label={t("reports.reportName", "Nome report")}
        className="h-7 flex-1 border-transparent bg-transparent text-sm shadow-none hover:border-border focus:border-border"
      />
      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={onDesigner}>
        {t("reports.openDesigner", "Designer")}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={onPreview}
        disabled={busyPreview}
      >
        {t("reports.previewHtml", "Anteprima")}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={onPdf}
        disabled={busyPdf}
      >
        PDF
      </Button>
      {canDelete && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-1.5 text-danger"
          aria-label={t("reports.deleteReport", "Elimina report")}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code provider editor — TS handler with the Output DTS generated from the
// Type + a params test-run. NO app binding: a Report Type is tenant-global,
// so ctx.db reaches EVERY app via the mandatory `slug/tabella` namespace.
// ---------------------------------------------------------------------------

function CodeProviderEditor({
  typeId,
  nodes,
  codeTs,
  onCodeTs,
  onDiagnostics,
}: {
  typeId: string;
  nodes: ReportTypeNode[];
  codeTs: string;
  onCodeTs: (v: string) => void;
  onDiagnostics: (errors: number) => void;
}) {
  const { t } = useTranslation();
  const appsQ = useQuery({ queryKey: ["apps"], queryFn: () => appsApi.list() });
  const apps = appsQ.data ?? [];
  // Every app's tables ride into the DTS as `slug/name` refs WITH their data
  // columns (the ADR-0047 names-not-GUIDs rule + the owner's slug-as-namespace
  // rule) — so `ctx.db.query('`, `column: '` and `rows[0].` all autocomplete
  // the real names. One aggregate fetch (tables + per-table detail), same
  // pattern as the builder's `columnsByTableQ`; small N, cached per app set.
  const appIdsKey = apps.map((a) => a.id).join(",");
  const refsQ = useQuery({
    queryKey: ["report-table-refs", appIdsKey],
    enabled: apps.length > 0,
    queryFn: async () => {
      const out: ReportTableRef[] = [];
      await Promise.all(
        apps.map(async (a) => {
          const tables = await appsApi.listTables(a.id);
          const details = await Promise.all(tables.map((tb) => appsApi.getTable(a.id, tb.id)));
          details.forEach((d, i) => {
            out.push({
              ref: `${a.slug}/${tables[i].name}`,
              columns: (d.columns ?? []).filter((c) => c.kind === "data").map((c) => c.name),
            });
          });
        }),
      );
      return out.sort((x, y) => x.ref.localeCompare(y.ref));
    },
  });
  const [paramsText, setParamsText] = React.useState("{}");
  const [result, setResult] = React.useState<string | null>(null);
  // The DTS follows the LIVE tree, so editing the schema retypes Output at once.
  // An `interface Output`/`Input` DECLARED IN THE BUFFER suppresses the ambient
  // twin (the user code is a global script, so both would MERGE and the
  // generated optional/nullable members conflict with the hand-written ones —
  // TS2717 red-flagged a valid handler and blocked the save). Booleans first so
  // the memo doesn't rebuild (and Monaco doesn't re-register the lib) on every
  // keystroke.
  const declaresOutput = /\binterface\s+Output\b/.test(codeTs);
  const declaresInput = /\binterface\s+Input\b/.test(codeTs);
  const ambientDts = React.useMemo(
    () =>
      reportProviderDts(nodes, refsQ.data ?? [], {
        includeOutput: !declaresOutput,
        includeInput: !declaresInput,
      }),
    [nodes, refsQ.data, declaresOutput, declaresInput],
  );

  const run = useMutation({
    mutationFn: () => {
      let params: Record<string, unknown> = {};
      try {
        params = JSON.parse(paramsText || "{}");
      } catch {
        throw new Error(t("reports.badParams", "Parametri: JSON non valido"));
      }
      return reportTypesApi.data(typeId, { params });
    },
    onSuccess: (res) => setResult(JSON.stringify(res.rows, null, 2)),
    onError: (e: Error) => setResult(e.message),
  });

  return (
    <div className="flex flex-col gap-3 p-3">
      <p className="text-xs text-muted-foreground">
        {t(
          "reports.crossAppHint",
          "Tutte le app sono disponibili: leggi le tabelle con ctx.db.query('slug_app/tabella').",
        )}
      </p>
      <CodeField
        label={t("reports.providerCodeLabel", "Handler (TypeScript)")}
        value={codeTs}
        onChange={onCodeTs}
        language="typescript"
        ambientDts={ambientDts}
        onDiagnostics={onDiagnostics}
        placeholder={REPORT_HANDLER_SKELETON}
      />
      <div className="flex items-start gap-2">
        <textarea
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
          aria-label={t("reports.runParams", "Parametri (JSON)")}
          rows={2}
          className="flex-1 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
        />
        <Button variant="outline" size="sm" onClick={() => run.mutate()} disabled={run.isPending}>
          {t("reports.run", "Esegui")}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {t(
          "reports.runHint",
          "Esegui usa il provider SALVATO — salva prima di provare le modifiche al codice.",
        )}
      </p>
      {result !== null && (
        <pre className="max-h-56 overflow-auto rounded-md border border-border bg-surface p-2 text-[11px]">
          {result}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page — master list + detail
// ---------------------------------------------------------------------------

export function ReportTypesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("reports", "write");
  const canDelete = can("reports", "delete");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const listQ = useQuery({ queryKey: ["report-types"], queryFn: reportTypesApi.list });
  const rows = listQ.data ?? [];
  const selected = rows.find((r) => r.id === selectedId) ?? rows[0] ?? null;

  const create = useMutation({
    mutationFn: () => {
      const n = rows.length + 1;
      return reportTypesApi.create({ name: `Tipo report ${n}`, schema: [] });
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ["report-types"] });
      setSelectedId(row.id);
    },
  });

  return (
    <PageContainer>
      {/* Divider under the header + FLUSH master/detail (no outer padding —
          `p-0` alone leaves the `sm:px-8/sm:pb-8` variants alive in twMerge,
          hence the explicit `sm:p-0`). The panes own their inner padding. */}
      <PageHeader
        title={t("reports.title", "Report")}
        description={t(
          "reports.subtitle",
          "Tipi report: il contratto dati (DTO) su cui disegni uno o più report.",
        )}
        className="border-b border-border pb-4 sm:pb-5"
      />
      <PageContent className="flex min-h-0 flex-1 gap-0 overflow-hidden p-0 sm:p-0">
        {/* Master — the app-pane sidebar pattern (AppTablesPane): uppercase
            section label + dashed "add" + icon rows with a small count. */}
        <aside className="flex w-64 shrink-0 flex-col border-r border-border">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
            <div className="mb-2 shrink-0 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("reports.sidebarTitle", "Tipi di report")}
            </div>
            {canWrite && (
              <button
                type="button"
                onClick={() => create.mutate()}
                disabled={create.isPending}
                className="mb-2 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-2 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("reports.newType", "Nuovo tipo")}
              </button>
            )}
            {rows.length === 0 && !listQ.isLoading ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">
                {t("reports.emptyBody", "Nessun tipo report. Creane uno con «Nuovo tipo».")}
              </p>
            ) : (
              rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  className={cn(
                    "mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px]",
                    selected?.id === r.id ? "bg-accent/15 text-accent" : "hover:bg-surface",
                  )}
                >
                  <FileType2
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                    strokeWidth={1.75}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{r.name}</span>
                  <span className="shrink-0 text-[9px] tabular-nums text-muted-foreground">
                    {r.schema.length}
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>
        <div className="flex min-h-0 flex-1 flex-col">
          {selected ? (
            <TypeDetail
              row={selected}
              canWrite={canWrite}
              canDelete={canDelete}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {t("reports.selectHint", "Seleziona o crea un tipo report.")}
            </div>
          )}
        </div>
      </PageContent>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// R2b/R2c — dynamic-options authoring ("Opzioni dinamiche") for a code/sql
// provider's declared params. The declaration (TS `interface Input` / sql
// binds) owns name/type/required; the options SOURCE can't be expressed
// there, so this editor declares it per param. Four sources: app TABLE,
// catalog DATASET, free SQL, fixed CHOICES — each with an explicit VALUE
// (what the param receives) + optional LABEL (what the user reads).
// ---------------------------------------------------------------------------

type ParamOptionsFrom = NonNullable<ReportParam["options_from"]>;
type OptionsKind = NonNullable<ParamOptionsFrom["kind"]>;

/** The saved provider's per-param `options_from`, keyed by param name. */
function optionsMapOf(params: ReportParam[] | undefined): Record<string, ParamOptionsFrom> {
  const out: Record<string, ParamOptionsFrom> = {};
  for (const p of params ?? []) if (p.options_from) out[p.name] = p.options_from;
  return out;
}

/** The COMPLETE, minimal wire shape of an in-progress declaration — or null
 *  when it's half-authored (dropped at save; the backend validator would 422
 *  a partial source, and a dropped row just means "free input"). */
export function completeOptions(of: ParamOptionsFrom | undefined): ParamOptionsFrom | null {
  if (!of) return null;
  const kind: OptionsKind = of.kind ?? "table";
  const label = of.label ? { label: of.label } : {};
  if (kind === "table" && of.table && of.value)
    return { kind, table: of.table, value: of.value, ...label };
  if (kind === "dataset" && of.dataset_id && of.value)
    return { kind, dataset_id: of.dataset_id, value: of.value, ...label };
  if (kind === "sql" && of.credential_id && of.sql?.trim())
    return {
      kind,
      credential_id: of.credential_id,
      sql: of.sql,
      ...(of.value ? { value: of.value } : {}),
      ...label,
    };
  if (kind === "static") {
    const choices = (of.choices ?? []).filter((c) => String(c.value ?? "").trim() !== "");
    if (choices.length)
      return {
        kind,
        choices: choices.map((c) => ({ value: c.value, ...(c.label ? { label: c.label } : {}) })),
      };
  }
  return null;
}

/** Merge the editor map's (complete) declaration onto a saved param. */
function withOptions(p: ReportParam, map: Record<string, ParamOptionsFrom>): ReportParam {
  const of = completeOptions(map[p.name]);
  if (of) return { ...p, options_from: of };
  if (!p.options_from) return p;
  const rest = { ...p };
  delete rest.options_from;
  return rest;
}

function ParamOptionsEditor({
  params,
  value,
  onChange,
  canWrite,
}: {
  params: ReportParam[];
  value: Record<string, ParamOptionsFrom>;
  onChange: (v: Record<string, ParamOptionsFrom>) => void;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-t border-border px-3 py-3">
      <div className="mb-1 text-xs font-medium">
        {t("reports.paramOptionsTitle", "Opzioni dinamiche dei parametri")}
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        {t(
          "reports.paramOptionsHint",
          "Un parametro con una sorgente diventa una tendina nei form di stampa. Sorgenti: tabella di un'app, dataset del catalogo, query SQL su una credenziale DB, o valori fissi. Per ognuna scegli la colonna VALORE (ciò che riceve il parametro) e, opzionale, l'ETICHETTA (il testo mostrato; vuota = valori distinti del valore). I parametri derivano dalla dichiarazione (interface Input / bind :nome), dopo il salvataggio.",
        )}
      </p>
      {params.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">
          {t(
            "reports.paramOptionsEmpty",
            "Nessun parametro dichiarato: aggiungi un'interface Input nel codice e salva.",
          )}
        </p>
      ) : (
        <div className="space-y-2">
          {params.map((p) => (
            <ParamOptionsRow
              key={p.name}
              param={p}
              value={value[p.name] ?? null}
              disabled={!canWrite}
              onChange={(of) => {
                const next = { ...value };
                if (of) next[p.name] = of;
                else delete next[p.name];
                onChange(next);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const OPTIONS_SEL_CLS =
  "h-7 rounded-md border border-border bg-background px-1.5 text-xs disabled:opacity-60";

function ParamOptionsRow({
  param,
  value,
  disabled,
  onChange,
}: {
  param: ReportParam;
  value: ParamOptionsFrom | null;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom | null) => void;
}) {
  const { t } = useTranslation();
  const kind: OptionsKind | "" = value ? (value.kind ?? "table") : "";
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="w-28 shrink-0 truncate font-mono" title={param.name}>
        {param.name}
      </span>
      <select
        value={kind}
        disabled={disabled}
        aria-label={t("reports.paramOptionsKind", "Sorgente")}
        onChange={(e) => {
          const k = e.target.value as OptionsKind | "";
          if (!k) onChange(null);
          else if (k === "static") onChange({ kind: k, choices: [{ value: "" }] });
          else onChange({ kind: k });
        }}
        className={OPTIONS_SEL_CLS}
      >
        <option value="">{t("reports.paramOptionsNoSource", "— nessuna tendina —")}</option>
        <option value="table">{t("reports.paramOptionsKindTable", "Tabella app")}</option>
        <option value="dataset">{t("reports.paramOptionsKindDataset", "Dataset")}</option>
        <option value="sql">{t("reports.paramOptionsKindSql", "Query SQL")}</option>
        <option value="static">{t("reports.paramOptionsKindStatic", "Valori fissi")}</option>
      </select>
      {kind === "table" && (
        <TableOptionsFields value={value!} disabled={disabled} onChange={onChange} />
      )}
      {kind === "dataset" && (
        <DatasetOptionsFields value={value!} disabled={disabled} onChange={onChange} />
      )}
      {kind === "sql" && (
        <SqlOptionsFields value={value!} disabled={disabled} onChange={onChange} />
      )}
      {kind === "static" && (
        <StaticChoicesFields value={value!} disabled={disabled} onChange={onChange} />
      )}
    </div>
  );
}

/** `table` kind — App → Tabella → Colonna valore → Etichetta pickers. */
function TableOptionsFields({
  value,
  disabled,
  onChange,
}: {
  value: ParamOptionsFrom;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom) => void;
}) {
  const { t } = useTranslation();
  const appsQ = useQuery({ queryKey: ["apps"], queryFn: () => appsApi.list() });
  const apps = appsQ.data ?? [];
  const storedSlug = (value.table ?? "").split("/")[0] || "";
  const storedTable = (value.table ?? "").split("/")[1] || "";
  // The app selection is LOCAL (a picked app with no table yet can't ride in
  // `value.table`); seeded once from the stored slug when the apps load.
  const [appId, setAppId] = React.useState("");
  React.useEffect(() => {
    if (!appId && storedSlug) {
      const a = apps.find((x) => x.slug === storedSlug);
      if (a) setAppId(a.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps.length, storedSlug]);
  const app = apps.find((a) => a.id === appId);
  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
    enabled: !!appId,
  });
  const table = (tablesQ.data ?? []).find((x) => x.name === storedTable);
  const tableQ = useQuery({
    queryKey: ["apps", appId, "tables", table?.id],
    queryFn: () => appsApi.getTable(appId, table!.id),
    enabled: !!appId && !!table,
  });
  const columns = (tableQ.data?.columns ?? []).filter((c) => c.kind === "data");
  return (
    <>
      <select
        value={appId}
        disabled={disabled}
        aria-label={t("reports.paramOptionsApp", "App")}
        onChange={(e) => {
          setAppId(e.target.value);
          onChange({ kind: "table" }); // a new app invalidates table/columns
        }}
        className={OPTIONS_SEL_CLS}
      >
        <option value="">{t("reports.paramOptionsPickApp", "App…")}</option>
        {apps.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      {app && (
        <select
          value={storedTable}
          disabled={disabled}
          aria-label={t("reports.paramOptionsTable", "Tabella")}
          onChange={(e) =>
            onChange(
              e.target.value
                ? { kind: "table", table: `${app.slug}/${e.target.value}` }
                : { kind: "table" },
            )
          }
          className={OPTIONS_SEL_CLS}
        >
          <option value="">{t("reports.paramOptionsPickTable", "Tabella…")}</option>
          {(tablesQ.data ?? []).map((x) => (
            <option key={x.id} value={x.name}>
              {x.name}
            </option>
          ))}
        </select>
      )}
      {app && table && (
        <ValueLabelSelects
          fields={["id", ...columns.map((c) => c.name)]}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      )}
    </>
  );
}

/** `dataset` kind — Dataset del catalogo → Campo valore → Etichetta pickers. */
function DatasetOptionsFields({
  value,
  disabled,
  onChange,
}: {
  value: ParamOptionsFrom;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom) => void;
}) {
  const { t } = useTranslation();
  const dsQ = useQuery({
    queryKey: ["datasets", "report-provider"],
    queryFn: () => datasetsApi.list(),
  });
  const dsId = value.dataset_id ?? "";
  const fieldsQ = useQuery({
    queryKey: ["datasets", dsId, "fields"],
    queryFn: () => datasetsApi.fields(dsId),
    enabled: !!dsId,
  });
  const fields = (fieldsQ.data ?? []).map((f) => f.name);
  return (
    <>
      <select
        value={dsId}
        disabled={disabled}
        aria-label={t("reports.paramOptionsDataset", "Dataset")}
        onChange={(e) =>
          onChange(
            e.target.value ? { kind: "dataset", dataset_id: e.target.value } : { kind: "dataset" },
          )
        }
        className={OPTIONS_SEL_CLS}
      >
        <option value="">{t("reports.paramOptionsPickDataset", "Dataset…")}</option>
        {(dsQ.data ?? []).map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      {dsId && (
        <ValueLabelSelects fields={fields} value={value} disabled={disabled} onChange={onChange} />
      )}
    </>
  );
}

/** `sql` kind — credenziale DB + SELECT read-only; value/label name the
 *  RESULT columns (free text — there is no introspection of an arbitrary
 *  SELECT; value empty = the first column). */
function SqlOptionsFields({
  value,
  disabled,
  onChange,
}: {
  value: ParamOptionsFrom;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom) => void;
}) {
  const { t } = useTranslation();
  const inputCls =
    "h-7 rounded-md border border-border bg-background px-1.5 font-mono text-xs disabled:opacity-60";
  return (
    <>
      <div className="w-52">
        <CredentialPicker
          value={value.credential_id ?? null}
          onChange={(id) => onChange({ ...value, credential_id: id })}
          type="postgres"
          disabled={disabled}
        />
      </div>
      <input
        value={value.sql ?? ""}
        disabled={disabled}
        aria-label={t("reports.paramOptionsSql", "SELECT")}
        placeholder="SELECT codice, descrizione FROM …"
        onChange={(e) => onChange({ ...value, sql: e.target.value })}
        className={cn(inputCls, "min-w-64 flex-1")}
      />
      <input
        value={value.value ?? ""}
        disabled={disabled}
        aria-label={t("reports.paramOptionsSqlValue", "Colonna valore")}
        placeholder={t("reports.paramOptionsSqlValuePh", "valore (vuoto = 1ª colonna)")}
        onChange={(e) => onChange({ ...value, value: e.target.value || null })}
        className={cn(inputCls, "w-40")}
      />
      <input
        value={value.label ?? ""}
        disabled={disabled}
        aria-label={t("reports.paramOptionsSqlLabel", "Colonna etichetta")}
        placeholder={t("reports.paramOptionsSqlLabelPh", "etichetta (vuota = valore)")}
        onChange={(e) => onChange({ ...value, label: e.target.value || null })}
        className={cn(inputCls, "w-40")}
      />
    </>
  );
}

/** `static` kind — fixed value/label rows with add/remove. */
function StaticChoicesFields({
  value,
  disabled,
  onChange,
}: {
  value: ParamOptionsFrom;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom) => void;
}) {
  const { t } = useTranslation();
  const choices = value.choices ?? [];
  const patch = (idx: number, p: Partial<{ value: string; label: string }>) =>
    onChange({
      ...value,
      choices: choices.map((c, i) =>
        i === idx
          ? {
              ...c,
              ...("value" in p ? { value: p.value ?? "" } : {}),
              ...("label" in p ? { label: p.label || null } : {}),
            }
          : c,
      ),
    });
  const inputCls =
    "h-7 w-28 rounded-md border border-border bg-background px-1.5 text-xs disabled:opacity-60";
  return (
    <div className="flex flex-col gap-1">
      {choices.map((c, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            value={String(c.value ?? "")}
            disabled={disabled}
            aria-label={t("reports.paramOptionsChoiceValue", "Valore")}
            placeholder={t("reports.paramOptionsChoiceValue", "Valore")}
            onChange={(e) => patch(i, { value: e.target.value })}
            className={inputCls}
          />
          <input
            value={c.label ?? ""}
            disabled={disabled}
            aria-label={t("reports.paramOptionsChoiceLabel", "Etichetta")}
            placeholder={t("reports.paramOptionsChoiceLabelPh", "Etichetta (opz.)")}
            onChange={(e) => patch(i, { label: e.target.value })}
            className={inputCls}
          />
          <button
            type="button"
            disabled={disabled}
            aria-label={t("reports.paramOptionsChoiceRemove", "Rimuovi valore")}
            onClick={() => onChange({ ...value, choices: choices.filter((_, j) => j !== i) })}
            className="text-muted-foreground hover:text-danger disabled:opacity-60"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange({ ...value, choices: [...choices, { value: "" }] })}
        className="self-start text-[11px] text-muted-foreground underline-offset-2 hover:underline disabled:opacity-60"
      >
        {t("reports.paramOptionsChoiceAdd", "+ Aggiungi valore")}
      </button>
    </div>
  );
}

/** The shared Valore/Etichetta pair over a known field list (table/dataset). */
function ValueLabelSelects({
  fields,
  value,
  disabled,
  onChange,
}: {
  fields: string[];
  value: ParamOptionsFrom;
  disabled: boolean;
  onChange: (v: ParamOptionsFrom) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <select
        value={value.value ?? ""}
        disabled={disabled}
        aria-label={t("reports.paramOptionsColumn", "Colonna valore")}
        onChange={(e) => onChange({ ...value, value: e.target.value || null })}
        className={OPTIONS_SEL_CLS}
      >
        <option value="">{t("reports.paramOptionsPickColumn", "Colonna valore…")}</option>
        {fields.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <select
        value={value.label ?? ""}
        disabled={disabled}
        aria-label={t("reports.paramOptionsLabel", "Etichetta")}
        onChange={(e) => onChange({ ...value, label: e.target.value || null })}
        className={OPTIONS_SEL_CLS}
      >
        <option value="">
          {t("reports.paramOptionsDistinct", "— uguale al valore (distinti) —")}
        </option>
        {fields.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
    </>
  );
}
